import { z } from 'zod';
/**
 * Stable identifiers used to authorize optional business-user notifications.
 * These IDs are deliberately independent from storage nesting and delivery
 * channels so the same policy can guard API responses, preference mutations,
 * in-app notifications, email, and SMS.
 */
export const NotificationCapabilityIds = [
    'marketplace.orderUpdates',
    'marketplace.orderCancellations',
    'marketplace.recurringOrders',
    'marketplace.productAlerts',
    'payouts.payoutUpdates',
    'reports.nightly',
    'reports.monthlyStatements',
    'reports.billingSummaries',
    'system.systemAlerts',
    'system.announcements',
    'system.accountActivity',
];
export const NotificationCapabilityIdSchema = z.enum(NotificationCapabilityIds);
export const ReportNotificationCapabilityIds = [
    'reports.nightly',
    'reports.monthlyStatements',
    'reports.billingSummaries',
];
export const NonReportNotificationCapabilityIds = NotificationCapabilityIds.filter((capabilityId) => !ReportNotificationCapabilityIds.includes(capabilityId));
const UniqueNotificationCapabilityIdsSchema = z
    .array(NotificationCapabilityIdSchema)
    .refine((capabilityIds) => new Set(capabilityIds).size === capabilityIds.length, {
    message: 'Notification capability IDs must be unique',
});
/**
 * Authorization ceiling for notification preferences on a business role.
 * `notificationPreferences` on the role remains a default-value layer only.
 */
export const NotificationRolePolicySchema = z
    .object({
    canReceive: UniqueNotificationCapabilityIdsSchema,
    canConfigure: UniqueNotificationCapabilityIdsSchema,
    visible: UniqueNotificationCapabilityIdsSchema,
})
    .superRefine(({ canReceive, canConfigure, visible }, context) => {
    const receivable = new Set(canReceive);
    const visibleCapabilities = new Set(visible);
    canConfigure.forEach((capabilityId, index) => {
        if (!receivable.has(capabilityId)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: `${capabilityId} must be receivable before it can be configurable`,
                path: ['canConfigure', index],
            });
        }
        if (!visibleCapabilities.has(capabilityId)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: `${capabilityId} must be visible before it can be configurable`,
                path: ['canConfigure', index],
            });
        }
    });
});
export const NotificationCapabilityStateSchema = z.object({
    visible: z.boolean(),
    canConfigure: z.boolean(),
    canReceive: z.boolean(),
    effective: z.boolean(),
});
export const NotificationCapabilityMaskEntrySchema = NotificationCapabilityStateSchema.extend({
    id: NotificationCapabilityIdSchema,
});
export const NotificationCapabilityMaskSchema = z.array(NotificationCapabilityMaskEntrySchema);
export const createNotificationRolePolicy = (capabilityIds) => ({
    canReceive: [...capabilityIds],
    canConfigure: [...capabilityIds],
    visible: [...capabilityIds],
});
export const DEFAULT_FULL_NOTIFICATION_ROLE_POLICY = createNotificationRolePolicy(NotificationCapabilityIds);
export const DEFAULT_NON_REPORT_NOTIFICATION_ROLE_POLICY = createNotificationRolePolicy(NonReportNotificationCapabilityIds);
